import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import type { MetricStatus } from '../../types';

/**
 * Score traffic-light dot (🟢/🟡/🔴) used in:
 *  - Inline narrative text ("78.8% 🟡 overall quality")
 *  - Quality Summary distribution bullets
 *  - Score column of the Column Quality Analysis table
 */

interface TrafficLightDotProps {
  bucket: 'excellent' | 'moderate' | 'poor';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const TrafficLightDot: React.FC<TrafficLightDotProps> = ({
  bucket, size = 'md', className = '',
}) => {
  const colors = {
    excellent: 'bg-cargo-success shadow-[0_0_8px_rgba(52,211,153,0.6)]',
    moderate:  'bg-cargo-warning shadow-[0_0_8px_rgba(251,191,36,0.6)]',
    poor:      'bg-cargo-error shadow-[0_0_8px_rgba(239,68,68,0.6)]',
  };
  const sizes = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5',
  };
  return (
    <span
      className={`inline-block rounded-full ${colors[bucket]} ${sizes[size]} ${className}`}
      aria-label={bucket}
    />
  );
};

/**
 * Status icon (✅/⚠️/❌) used in:
 *  - Fill Rate / Uniqueness / Invalid Count columns
 *  - Per-row metric cells
 */

interface StatusIconProps {
  status: MetricStatus;
  className?: string;
  size?: 'sm' | 'md';
}

export const StatusIcon: React.FC<StatusIconProps> = ({ status, className = '', size = 'md' }) => {
  const sizeCls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  if (status === 'ok') {
    return <CheckCircle2 className={`${sizeCls} text-cargo-success ${className}`} />;
  }
  if (status === 'warn') {
    return <AlertTriangle className={`${sizeCls} text-cargo-warning ${className}`} />;
  }
  return <XCircle className={`${sizeCls} text-cargo-error ${className}`} />;
};

/**
 * Combined value + status icon cell — flex layout that stacks vertically
 * when narrow (e.g. "100% on top, ✅ below") OR sits inline when value
 * is short (e.g. "0 ✅"). Auto-selects layout based on value length.
 */

interface MetricCellProps {
  value: number | null;
  status: MetricStatus;
  format?: 'percent' | 'integer';
  className?: string;
}

export const MetricCell: React.FC<MetricCellProps> = ({
  value, status, format = 'percent', className = '',
}) => {
  // null/undefined = not measured → show "—" (never a fake 0%)
  if (value === null || value === undefined) {
    return (
      <span className={`text-[13px] text-cargo-muted ${className}`} title="Not measured">
        —
      </span>
    );
  }
  const display =
    format === 'percent' ? `${value.toFixed(1)}%` : value.toLocaleString();

  // Short values (like "0") render inline; longer ones stack
  const isShort = display.length <= 3;
  const layout = isShort ? 'flex-row items-center gap-1.5' : 'flex-col items-start gap-1';

  return (
    <span className={`inline-flex ${layout} ${className}`}>
      <span className="text-[13px] text-cargo-text font-medium">{display}</span>
      <StatusIcon status={status} size="sm" />
    </span>
  );
};

/**
 * Score cell — value + traffic-light dot. Always stacks (matches Reltio
 * for the Score column).
 */

interface ScoreCellProps {
  value: number | null;
  bucket: 'excellent' | 'moderate' | 'poor';
  className?: string;
}

export const ScoreCell: React.FC<ScoreCellProps> = ({ value, bucket, className = '' }) => {
  if (value === null || value === undefined) {
    return (
      <span className={`text-[13px] text-cargo-muted ${className}`} title="Not measured — rules could not be evaluated">
        —
      </span>
    );
  }
  return (
    <span className={`inline-flex flex-col items-start gap-1 ${className}`}>
      <span className="text-[13px] text-cargo-text font-medium">{value.toFixed(1)}%</span>
      <TrafficLightDot bucket={bucket} size="md" />
    </span>
  );
};
